import { Badge, Button, CollectionPreferencesProps, SpaceBetween, TableProps } from '@amzn/awsui-components-core';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ApplicationFlatTableEntity } from 'src/components/context/AppContextModels';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { getReadableDateTimeFormat } from 'src/utilities/DateTimeUtilities';
import { AppNameWithLink } from './AllApps';

export const DEFAULT_VISIBLE_CONTENT = [
  'applicationName',
  'applicationLink',
  'shortApplicationDescription',
  'applicationOwner',
  'glAccount',
  'toolUI',
  'edit'
];

export const DEFAULT_VISIBLE_CONTENT_OPTIONS: CollectionPreferencesProps.VisibleContentOptionsGroup[] = [
  {
    label: 'Properties',
    options: [
      { id: 'applicationName', label: 'Application', editable: false },
      { id: 'applicationLink', label: 'Open', editable: false },
      { id: 'shortApplicationDescription', label: 'Description', editable: false },
      { id: 'applicationOwner', label: 'Team', editable: true },
      { id: 'glAccount', label: 'Module', editable: true },
      { id: 'toolUI', label: 'Product UI', editable: true },
      { id: 'updatedBy', label: 'Updated By', editable: true },
      { id: 'updatedTime', label: 'Updated At', editable: true }
    ]
  }
];

export const APPLICATIONS_DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  visibleContent: DEFAULT_VISIBLE_CONTENT,
  wrapLines: false,
  stripedRows: false,
  stickyColumns: { first: 0 }
};

export const APPLICATION_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<ApplicationFlatTableEntity>[] = [
  {
    id: 'applicationName',
    header: 'Application',
    width: 230,
    cell: (item: ApplicationFlatTableEntity) => (
      <>
        <SpaceBetween size="m" direction="horizontal">
          <AppNameWithLink
            appName={item.applicationName}
            appId={item.id}
            embeddingEnabled={item.embeddingEnabled}
            applicationLink={item.applicationLink}
          />
        </SpaceBetween>
      </>
    ),
    sortingField: 'applicationName'
  },
  {
    id: 'shortApplicationDescription',
    header: 'Description',
    cell: (item) => item.shortApplicationDescription,
    sortingField: 'shortApplicationDescription'
  },
  {
    id: 'applicationOwner',
    header: 'Team',
    width: 150,
    cell: (item) => item.applicationOwner,
    sortingField: 'applicationOwner'
  },
  {
    id: 'glAccount',
    header: 'Module',
    width: 250,
    cell: (item: ApplicationFlatTableEntity) => item.glAccount,
    sortingField: 'glAccount'
  },
  {
    id: 'toolUI',
    header: 'Product UI',
    width: 175,
    cell: (item) => item.toolUI,
    sortingField: 'toolUI'
  },
  {
    id: 'updatedBy',
    header: 'Updated By',
    width: 150,
    cell: (item) => item.updatedBy,
    sortingField: 'updatedBy'
  },
  {
    id: 'updatedTime',
    header: 'Updated At',
    width: 150,
    cell: (item) => getReadableDateTimeFormat(item.updatedTime),
    sortingField: 'updatedTime'
  },
  {
    id: 'edit',
    header: 'Edit',
    minWidth: 50,
    cell: (item) => <AllAppsActionButtonDropdown item={item} />
  }
];

interface AllAppsActionButtonDropdownProps {
  item: ApplicationFlatTableEntity;
}
const AllAppsActionButtonDropdown: React.FC<AllAppsActionButtonDropdownProps> = ({ item }) => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  if (!isAdmin) return null;

  return (
    <button
      type="button"
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      aria-label={`Edit ${item.applicationName}`}
      onClick={() => navigate('/admin/manage-applications/' + item.id)}
    >
      <FontAwesomeIcon icon={faWrench} />
    </button>
  );
};
